import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  FormControlLabel,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  Collapse,
  IconButton
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  SentimentSatisfiedAlt,
  SentimentNeutral,
  SentimentDissatisfied,
  ExpandMore,
  ExpandLess,
  ContentCopy
} from '@mui/icons-material';
import { operationalRadii, operationalScrollbars } from '../../../design-system/tokens';

const SENTIMENT_CONFIG = {
  positive: {
    label: 'Positivo',
    icon: SentimentSatisfiedAlt,
    color: 'success'
  },
  neutral: {
    label: 'Neutro',
    icon: SentimentNeutral,
    color: 'warning'
  },
  negative: {
    label: 'Negativo',
    icon: SentimentDissatisfied,
    color: 'error'
  }
};

const SentimentPropagationDialog = ({
  open,
  onClose,
  onConfirm,
  onCancel,
  sentiment,
  similarArticles = [],
  loading = false
}) => {
  const theme = useTheme();
  const [rememberChoice, setRememberChoice] = useState(false);
  const [showArticles, setShowArticles] = useState(false);

  const config = SENTIMENT_CONFIG[sentiment] || SENTIMENT_CONFIG.neutral;
  const SentimentIcon = config.icon;
  const count = similarArticles.length;

  const handleConfirm = () => {
    onConfirm(rememberChoice ? 'always' : null);
  };

  const handleCancel = () => {
    onCancel(rememberChoice ? 'never' : null);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: `${operationalRadii.modal}px`,
          maxWidth: 480,
          m: 2,
          backgroundColor: theme.palette.background.paper,
          backgroundImage: 'none',
        }
      }}
    >
      <DialogContent sx={{ pt: 3, pb: 2, px: 3 }}>
        {/* Header com ícone */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
          <ContentCopy sx={{ color: 'primary.main', fontSize: 24, mt: 0.25, flexShrink: 0 }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ lineHeight: 1.4, mb: 0.5 }}>
              Artigos similares encontrados
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
              Encontramos <strong>{count}</strong> artigo{count !== 1 ? 's' : ''} similar{count !== 1 ? 'es' : ''} a este.
              Deseja aplicar o sentimento <Chip
                size="small"
                icon={<SentimentIcon sx={{ fontSize: 16 }} />}
                label={config.label}
                color={config.color}
                sx={{ height: 22, mx: 0.5 }}
              /> para todos?
            </Typography>
          </Box>
        </Box>

        {/* Lista colapsável de artigos */}
        {count > 0 && (
          <Box sx={{ mt: 2 }}>
            <Button
              size="small"
              onClick={() => setShowArticles(!showArticles)}
              endIcon={showArticles ? <ExpandLess /> : <ExpandMore />}
              sx={{ textTransform: 'none', color: 'text.secondary', mb: 1 }}
            >
              {showArticles ? 'Ocultar artigos' : `Ver ${count} artigo${count !== 1 ? 's' : ''}`}
            </Button>
            <Collapse in={showArticles}>
              <List
                dense
                sx={{
                  maxHeight: 300,
                  overflow: 'auto',
                  bgcolor: 'action.hover',
                  borderRadius: `${operationalRadii.surface}px`,
                  '& .MuiListItem-root': {
                    py: 0.5
                  },
                  ...operationalScrollbars.thin
                }}
              >
                {similarArticles.map((article) => (
                  <ListItem key={article._id} disablePadding sx={{ px: 1.5 }}>
                    <ListItemAvatar sx={{ minWidth: 40 }}>
                      <Avatar
                        src={article.image}
                        variant="rounded"
                        sx={{ width: 32, height: 32 }}
                      >
                        {article.siteName?.[0]}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={article.title}
                      secondary={article.siteName}
                      primaryTypographyProps={{
                        variant: 'body2',
                        noWrap: true,
                        sx: { fontWeight: 500 }
                      }}
                      secondaryTypographyProps={{
                        variant: 'caption',
                        noWrap: true
                      }}
                    />
                    <Chip
                      size="small"
                      label={`${article.similarityScore}%`}
                      sx={{ height: 20, fontSize: 11 }}
                    />
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </Box>
        )}

        {/* Checkbox para lembrar escolha */}
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={rememberChoice}
              onChange={(e) => setRememberChoice(e.target.checked)}
            />
          }
          label={
            <Typography variant="body2" color="text.secondary">
              Lembrar minha escolha
            </Typography>
          }
          sx={{ mt: 2 }}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, gap: 1.5 }}>
        <Button
          onClick={handleCancel}
          variant="text"
          disabled={loading}
          sx={{
            minWidth: 80,
            textTransform: 'none',
            fontWeight: 500
          }}
        >
          Apenas este
        </Button>
        <Button
          onClick={handleConfirm}
          color="primary"
          variant="contained"
          disabled={loading}
          sx={{
            minWidth: 120,
            textTransform: 'none',
            fontWeight: 500
          }}
        >
          {loading ? 'Aplicando...' : `Aplicar em ${count + 1}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

SentimentPropagationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  sentiment: PropTypes.oneOf(['positive', 'neutral', 'negative']).isRequired,
  similarArticles: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      siteName: PropTypes.string,
      image: PropTypes.string,
      similarityScore: PropTypes.number
    })
  ),
  loading: PropTypes.bool
};

export default SentimentPropagationDialog;
